import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, CircularProgress, Chip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import VideocamIcon from '@mui/icons-material/Videocam';
import GavelIcon from '@mui/icons-material/Gavel';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface Notice {
  id: string;
  title: string;
  description: string;
  date: string;
  category: 'general' | 'urgent' | 'event' | 'closure' | 'other';
  validUntil: string;
  imageUrl?: string;
}

export default function Notices() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotices();
  }, []);

  const loadNotices = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'news_section_notices'));
      const data: Notice[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as Notice);
      });
      data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setNotices(data);
    } catch (error) {
      console.error('Error loading notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryConfig = (category: string) => {
    switch (category) {
      case 'urgent': return { gradient: ['#ef4444', '#dc2626'] as const, label: 'URGENT', bg: 'rgba(239,68,68,0.08)' };
      case 'event': return { gradient: ['#22c55e', '#16a34a'] as const, label: 'EVENT', bg: 'rgba(34,197,94,0.08)' };
      case 'closure': return { gradient: ['#f59e0b', '#d97706'] as const, label: 'CLOSURE', bg: 'rgba(245,158,11,0.08)' };
      case 'general': return { gradient: ['#4f46e5', '#6366f1'] as const, label: 'GENERAL', bg: 'rgba(79,70,229,0.08)' };
      default: return { gradient: ['#64748b', '#475569'] as const, label: 'OTHER', bg: 'rgba(100,116,139,0.08)' };
    }
  };

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Notices & Circulars — Kottayil Temple" description="Official notices and circulars from Kottayil Temple (Kotta-Kovilakam Kshethram). Administrative communications and important updates." path="/news/notices" keywords="Kottayil temple notices, circulars, official notices, temple communications, കൊട്ടയിൽ ക്ഷേത്രം നോട്ടീസുകൾ" />
      <PageBanner title="Notices & Circulars" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{ borderRadius: '24px', overflow: 'hidden', mb: 3.5, boxShadow: '0 20px 60px rgba(79,70,229,0.15)' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #1e1b4b 0%, #4f46e5 55%, #a5b4fc 100%)',
                px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 }, pb: { xs: 5, sm: 6 },
                position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  }}>
                    <AssignmentIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', fontSize: { xs: '1.8rem', sm: '2.4rem' }, mb: 1.5, lineHeight: 1.1 }}>
                    Notices & Circulars
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.7, maxWidth: 500, mb: 2.5 }}>
                    Important circulars, temple closures, event notices, and official communications from the temple administration.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<AssignmentIcon sx={{ fontSize: 16, color: '#4f46e5 !important' }} />} label="Official Notices" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#4f46e5', fontWeight: 700, fontSize: '0.7rem', height: 28, borderRadius: '14px' }} />
                    <Chip icon={<GavelIcon sx={{ fontSize: 16, color: '#6366f1 !important' }} />} label="Circulars" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 28, borderRadius: '14px', backdropFilter: 'blur(10px)' }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* PHOTOGRAPHY & VIDEOGRAPHY NOTICE */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              mb: 3.5,
            }}>
              {/* Gradient header */}
              <Box sx={{
                background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                px: { xs: 2.5, sm: 3 }, py: 2,
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '12px',
                  background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                  border: '1.5px solid rgba(255,255,255,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <VideocamIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#fff', lineHeight: 1.3 }}>
                    Photography &amp; Videography
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Temple Guidelines
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                {/* Pricing card */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 },
                  bgcolor: 'rgba(46,125,50,0.04)', borderRadius: '16px',
                  border: '1px solid rgba(46,125,50,0.12)',
                  px: { xs: 2, sm: 2.5 }, py: { xs: 1.8, sm: 2 },
                }}>
                  <Box sx={{
                    minWidth: 52, height: 52, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 6px 20px rgba(46,125,50,0.25)',
                  }}>
                    <CurrencyRupeeIcon sx={{ fontSize: 26, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '0.96rem' }, color: '#1a1a1a', lineHeight: 1.6 }}>
                      <Box component="span" sx={{ color: '#2e7d32', fontWeight: 900, fontSize: { xs: '1.15rem', sm: '1.25rem' } }}>₹400</Box>
                      {' '}must be remitted in advance at the temple counter and obtain permission for videography.
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: '#777', mt: 0.5, lineHeight: 1.6 }}>
                      Kindly contact the temple office for prior approval before any photography or videography inside the temple premises.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#4f46e5' }} />
              </Box>
            ) : notices.length === 0 ? (
              <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 4, border: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#aaa' }}>No notices available at this time.</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {notices.map((notice) => {
                  const cc = getCategoryConfig(notice.category);
                  return (
                    <Box key={notice.id} sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.06)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                      transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 48px rgba(0,0,0,0.08)' },
                    }}>
                      {/* Gradient header */}
                      <Box sx={{
                        background: `linear-gradient(135deg, ${cc.gradient[0]}, ${cc.gradient[1]})`,
                        px: { xs: 2.5, sm: 3 }, py: 1.8,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: '10px',
                            background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                            border: '1.5px solid rgba(255,255,255,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <DescriptionIcon sx={{ fontSize: 18, color: '#fff' }} />
                          </Box>
                          <Box sx={{ px: 1.2, py: 0.3, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              {cc.label}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarMonthIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }} />
                          <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                            {new Date(notice.date).toLocaleDateString('en-IN')}
                          </Typography>
                        </Box>
                      </Box>
                      {notice.imageUrl && (
                        <Box component="img" src={notice.imageUrl} alt={notice.title}
                          sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                        />
                      )}
                      <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#222', mb: 1 }}>
                          {notice.title}
                        </Typography>
                        {notice.validUntil && (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.4, borderRadius: '10px', bgcolor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', mb: 1.5 }}>
                            <ScheduleIcon sx={{ fontSize: 13, color: '#ef4444' }} />
                            <Typography sx={{ fontSize: '0.65rem', color: '#ef4444', fontWeight: 700 }}>
                              Valid until: {new Date(notice.validUntil).toLocaleDateString('en-IN')}
                            </Typography>
                          </Box>
                        )}
                        <Typography sx={{ fontSize: '0.82rem', color: '#666', lineHeight: 1.8 }}>
                          {notice.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* NOTICE BOARD FOOTER */}
            <Box sx={{
              mt: 3.5, borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              boxShadow: '0 8px 32px rgba(15,23,42,0.3)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3, position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <InfoOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>Notice Board</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, maxWidth: 500, mx: 'auto' }}>
                  Physical notice board is available at the temple entrance. Important circulars are also announced during daily poojas.
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="news" activePath="/news/notices" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
